import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

// Windowed list: only the rows in (or near) view are mounted, so a list of
// thousands costs the same as a list of twenty.
//
// `itemHeight` is a number, or a function (item, index) => number for rows of
// differing heights. With `maxHeight` the list sizes itself to its content up
// to that height (dropdowns); otherwise it fills its container (panels).
const FALLBACK_HEIGHT = 600;

function firstIndexAt(offsets, y) {
  let lo = 0;
  let hi = offsets.length - 2;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (offsets[mid] <= y) lo = mid;
    else hi = mid - 1;
  }
  return Math.max(0, lo);
}

export default function VirtualList({
  items,
  itemHeight,
  renderItem,
  overscan = 6,
  maxHeight,
  padding = 0,
  scrollToIndex,
  resetKey,
  className = "",
  ...rest
}) {
  const ref = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewHeight, setViewHeight] = useState(0);
  const fixed = typeof itemHeight === "number";

  const offsets = useMemo(() => {
    if (fixed) return null;
    const arr = new Float64Array(items.length + 1);
    for (let i = 0; i < items.length; i++) arr[i + 1] = arr[i] + itemHeight(items[i], i);
    return arr;
  }, [items, itemHeight, fixed]);

  const offsetOf = (i) => (fixed ? i * itemHeight : offsets[i]);
  const total = offsetOf(items.length);
  const height = maxHeight != null ? Math.min(total + padding * 2, maxHeight) : undefined;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || maxHeight != null) return undefined;
    const measure = () => setViewHeight(el.clientHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxHeight]);

  useEffect(() => {
    if (ref.current) ref.current.scrollTop = 0;
    setScrollTop(0);
  }, [resetKey]);

  useEffect(() => {
    const el = ref.current;
    if (!el || scrollToIndex == null || scrollToIndex < 0 || scrollToIndex >= items.length) return;
    const top = offsetOf(scrollToIndex) + padding;
    const bottom = offsetOf(scrollToIndex + 1) + padding;
    const view = el.clientHeight || height || FALLBACK_HEIGHT;
    let next = null;
    if (top - padding < el.scrollTop) next = top - padding;
    else if (bottom + padding > el.scrollTop + view) next = bottom + padding - view;
    if (next == null) return;
    el.scrollTop = next;
    setScrollTop(next); // render the target rows now; the scroll event syncs the real offset
    // offsetOf is derived from items/itemHeight, which scrollToIndex follows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToIndex]);

  const view = (maxHeight != null ? height : viewHeight) || FALLBACK_HEIGHT;
  const y = Math.max(0, scrollTop - padding);
  let start;
  let end;
  if (fixed) {
    start = Math.floor(y / itemHeight);
    end = Math.ceil((y + view) / itemHeight);
  } else {
    start = firstIndexAt(offsets, y);
    end = firstIndexAt(offsets, y + view) + 1;
  }
  start = Math.max(0, start - overscan);
  end = Math.min(items.length, end + overscan);

  const rows = [];
  for (let i = start; i < end; i++) {
    const top = offsetOf(i);
    rows.push(
      renderItem(items[i], i, {
        position: "absolute",
        top,
        left: 0,
        right: 0,
        height: offsetOf(i + 1) - top,
      }),
    );
  }

  return (
    <div
      ref={ref}
      className={className}
      style={{ overflowY: "auto", position: "relative", height, padding }}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      {...rest}
    >
      <div style={{ position: "relative", height: total }}>{rows}</div>
    </div>
  );
}
